import PropTypes from 'prop-types';
import { useId } from 'react';
import styled from 'styled-components';

import { Box, Flex } from '../../grid';
import { Icon } from '../../icons';

const clampFraction = (current, target) => {
  if (!target || target <= 0) return 0;
  const ratio = current / target;
  if (Number.isNaN(ratio) || ratio < 0) return 0;
  if (ratio > 1) return 1;
  return ratio;
};

const ProgressBar = ({
  currentCents,
  targetCents,
  qualifyingMessage,
  cta = '',
  onCtaClick = null,
  ctaShowChevron = false,
  expanded = false,
  showBar = true,
  children = null,
}) => {
  const fraction = clampFraction(currentCents, targetCents);
  const percent = `${(fraction * 100).toFixed(2)}%`;
  const nestedContentId = useId();

  return (
    <Wrapper
      bg="warmWhite100"
      borderRadius="8px"
      px="20px"
      py="16px"
      width="100%"
      style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
    >
      <Flex alignItems="baseline" style={{ gap: '16px' }}>
        <MessageColumn>
          <MessageText>{qualifyingMessage}</MessageText>
        </MessageColumn>
        {cta &&
          (onCtaClick ? (
            <CtaButton
              type="button"
              onClick={onCtaClick}
              aria-expanded={ctaShowChevron ? expanded : undefined}
              aria-controls={ctaShowChevron ? nestedContentId : undefined}
            >
              {cta}
              {ctaShowChevron && (
                <ChevronWrapper $expanded={expanded}>
                  <Icon name="chevron" fill="black" width="16px" height="16px" />
                </ChevronWrapper>
              )}
            </CtaButton>
          ) : (
            <CtaText>{cta}</CtaText>
          ))}
      </Flex>
      {showBar && (
        <Track
          role="progressbar"
          aria-valuenow={currentCents}
          aria-valuemin={0}
          aria-valuemax={targetCents}
          aria-label={qualifyingMessage}
        >
          <Fill style={{ width: percent }} />
        </Track>
      )}
      {children && <NestedContent id={nestedContentId}>{children}</NestedContent>}
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  box-sizing: border-box;
`;

const MessageColumn = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  padding-top: 2px;
`;

const MessageText = styled.span`
  font-family: Barlow, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  color: #000000;
  margin: 0;
`;

const CtaText = styled.span`
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  text-decoration: underline;
  color: #000000;
  flex: 0 0 auto;
  margin: 0;
`;

const CtaButton = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  text-decoration: underline;
  color: #000000;
  flex: 0 0 auto;
  white-space: nowrap;

  &:hover {
    opacity: 0.7;
  }

  /* Keyboard focus keeps a visible indicator (WCAG 2.1 SC 2.4.7); mouse-click
     focus stays clean since :focus-visible doesn't match pointer interactions. */
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
    opacity: 0.7;
  }
`;

const NestedContent = styled.div`
  /* The Wrapper's 8px flex gap is too tight above the nested gift list; add an
     extra 8px so the spacing above it matches the card's 16px bottom padding. */
  margin-top: 8px;
`;

const ChevronWrapper = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  transition: transform 200ms ease;
  transform: rotate(${props => (props.$expanded ? '180deg' : '0deg')});
`;

const Track = styled.div`
  position: relative;
  width: 100%;
  height: 12px;
  background-color: #ffffff;
  border-radius: 6px;
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  background-color: #e81313;
  border-radius: 6px;
  transition: width 400ms ease-in-out;
`;

ProgressBar.propTypes = {
  currentCents: PropTypes.number.isRequired,
  targetCents: PropTypes.number.isRequired,
  qualifyingMessage: PropTypes.string.isRequired,
  cta: PropTypes.string,
  onCtaClick: PropTypes.func,
  // When true, render a chevron after the CTA that rotates with `expanded` —
  // used for the qualified state's expand/collapse "Choose gift" toggle.
  ctaShowChevron: PropTypes.bool,
  expanded: PropTypes.bool,
  // When false, render the card without the progress track — used for
  // pluQuantity campaigns, which share this container (message + "Choose
  // gift" toggle + nested picker) but have no spend threshold to visualise.
  showBar: PropTypes.bool,
  // Optional content rendered inside the card below the bar (e.g. the gift
  // picker when the campaign is qualified for).
  children: PropTypes.node,
};

ProgressBar.defaultProps = {
  cta: '',
  onCtaClick: null,
  ctaShowChevron: false,
  expanded: false,
  showBar: true,
  children: null,
};

export { ProgressBar };
